import { Maybe, OccupationType } from '@/lib/generated/graphql-types'
import {
  DropdownWithSlugId,
  SalaryFilterControls,
  SalaryFiltersParams,
} from '@/modules/salary/types'
import { SalaryFilterParam } from '../constants'
import { SalaryFilterControl } from '../constants'
import {
  FULL_TIME_VALUE,
  SALARY_FULL_TIME_VALUE,
} from '@/modules/surveys/configs/onboarding/current-job-pane-config'
import {
  PHYSICIAN_SLUG,
  professionSlugs,
} from '@/modules/surveys/configs/onboarding/personal-info-config'
import { SalaryPayPeriod } from '../table/constants'
import { states } from '@/lib/states'
import { pick } from 'lodash'

export function getFilterControlValues(
  filterValues: Partial<SalaryFiltersParams> | null,
) {
  return pick(filterValues, [
    SalaryFilterControl.profession,
    SalaryFilterControl.specialty,
    SalaryFilterControl.state,
    SalaryFilterControl.jobType,
  ])
}

export async function convertSalaryFiltersControlsToUrlParams(
  filterControls: Partial<SalaryFilterControls>,
  getOccupationByProfessionAndSpecialty: (opts: {
    professionSlug: string
    specialtySlug: string
  }) => Promise<OccupationType | undefined>,
): Promise<Partial<SalaryFiltersParams>> {
  function filterToString(
    filter: DropdownWithSlugId | string | null | undefined,
  ) {
    if (typeof filter === 'string') {
      return filter
    }
    return filter?.slugId ?? ''
  }
  const professionSlug = filterToString(filterControls.profession)
  const occupation = await getOccupationByProfessionAndSpecialty({
    professionSlug,
    specialtySlug: filterToString(filterControls.specialty),
  })

  return {
    [SalaryFilterParam.occupation]: occupation
      ? occupation.slug
      : professionSlug,
    [SalaryFilterParam.jobType]: filterToString(filterControls.jobType),
    [SalaryFilterParam.location]: filterToString(filterControls.state),
    [SalaryFilterParam.page]: filterToString(filterControls.page),
    [SalaryFilterParam.sortBy]: filterToString(filterControls.sortBy),
    [SalaryFilterParam.payPeriod]: filterToString(
      filterControls.payPeriod,
    ) as SalaryPayPeriod,
  }
}

export function getJobTypeSalaryFiltersFromUrl(jobType: string | undefined) {
  return jobType && jobType !== FULL_TIME_VALUE
    ? jobType
    : SALARY_FULL_TIME_VALUE
}

export function getStateSalaryFiltersFromUrl({
  location,
}: {
  location: string | undefined
}) {
  const stateLocation = states?.find(
    (state) => state.slug?.toLowerCase?.() === location?.toLowerCase?.(),
  )
  if (!stateLocation) {
    return null
  }
  return {
    id: stateLocation.code,
    label: stateLocation.name,
    slugId: stateLocation.code,
  }
}

export function getSalaryFilterControlsFromUrl(opts?: {
  searchParams?: URLSearchParams | null
  userProfessionSlug?: string
  occupation?: Maybe<OccupationType> | undefined
  jobType: string | undefined
  occupationSlug: string | undefined
  location: string | undefined
}): SalaryFilterControls {
  const searchParams = opts?.searchParams

  const profession =
    opts?.occupationSlug &&
    professionSlugs.includes(opts.occupationSlug as string)
      ? opts.occupationSlug
      : opts?.occupation?.profession?.slug ||
        opts?.userProfessionSlug ||
        PHYSICIAN_SLUG

  let specialtyFilter = ''
  let payPeriodFilter = ''
  let sortByFilter = ''
  let page = ''
  if (searchParams && typeof searchParams?.get === 'function') {
    specialtyFilter = searchParams.get?.(SalaryFilterControl.specialty) ?? ''
    payPeriodFilter = searchParams.get?.(SalaryFilterControl.payPeriod) ?? ''
    sortByFilter = searchParams?.get(SalaryFilterControl.sortBy) ?? ''
    page = searchParams.get(SalaryFilterControl.page) ?? ''
  }

  return {
    [SalaryFilterControl.specialty]:
      (opts?.occupation?.specialty?.slug || specialtyFilter) ?? '',
    [SalaryFilterControl.jobType]: getJobTypeSalaryFiltersFromUrl(
      opts?.jobType,
    ),
    [SalaryFilterControl.profession]: profession,
    [SalaryFilterControl.state]: getStateSalaryFiltersFromUrl({
      location: opts?.location,
    }),
    [SalaryFilterControl.payPeriod]: payPeriodFilter as SalaryPayPeriod | null,
    [SalaryFilterControl.sortBy]: sortByFilter,
    [SalaryFilterControl.page]: page,
  }
}
